import { useEffect, useRef, useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { authHeaders, getAccessToken } from '@/api/client';

export function useNotificationStream() {
  const queryClient = useQueryClient();
  const abortRef = useRef<AbortController | null>(null);
  const retryTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const retryCountRef = useRef(0);

  const connect = useCallback(() => {
    const token = getAccessToken();
    if (!token) return;

    const controller = new AbortController();
    abortRef.current = controller;

    const baseUrl = import.meta.env.VITE_API_URL || '/api';

    fetch(`${baseUrl}/notifications/stream`, {
      headers: authHeaders(),
      signal: controller.signal,
    })
      .then(async (response) => {
        if (!response.ok || !response.body) return;

        retryCountRef.current = 0;
        const reader = response.body.getReader();
        const decoder = new TextDecoder();
        let buffer = '';

        while (true) {
          const { done, value } = await reader.read();
          if (done) break;

          buffer += decoder.decode(value, { stream: true });
          const messages = buffer.split('\n\n');
          buffer = messages.pop() ?? '';

          for (const msg of messages) {
            const eventMatch = msg.match(/^event:\s*(.+)$/m);
            const dataMatch = msg.match(/^data:\s*(.+)$/m);
            if (!eventMatch || !dataMatch) continue;

            const eventType = eventMatch[1]!;
            if (eventType === 'notification') {
              try {
                const notification = JSON.parse(dataMatch[1]!);
                queryClient.invalidateQueries({ queryKey: ['notifications', 'unread-count'] });
                queryClient.invalidateQueries({ queryKey: ['notifications'] });
                toast(notification.title, {
                  description: notification.message,
                });
              } catch {
                // Invalid JSON — skip
              }
            }
          }
        }
      })
      .catch((err) => {
        if (err.name === 'AbortError') return;
      })
      .finally(() => {
        if (!controller.signal.aborted) {
          const delay = Math.min(1000 * 2 ** retryCountRef.current, 30_000);
          retryCountRef.current++;
          retryTimeoutRef.current = setTimeout(connect, delay);
        }
      });
  }, [queryClient]);

  useEffect(() => {
    connect();

    return () => {
      if (abortRef.current) abortRef.current.abort();
      if (retryTimeoutRef.current) clearTimeout(retryTimeoutRef.current);
    };
  }, [connect]);
}
